import { useEffect, useState } from 'react';
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { db } from './firebase';

export interface LiveVisitor {
  sessionId: string;
  lastActive: number;
  page: string;
  path: string;
  device: 'Mobile' | 'Desktop' | 'Tablet';
  city?: string;
  source?: string;
}

export interface LiveVisitorsStats {
  activeCount: number;
  visitors: LiveVisitor[];
  pageBreakdown: Record<string, number>;
  deviceBreakdown: { mobile: number; desktop: number };
}

const VISITORS_COLLECTION = 'active_visitors';
const SESSION_STORAGE_KEY = 'healthstore_visitor_session_v1';
const HEARTBEAT_INTERVAL_MS = 20000; // 20s heartbeat
const ACTIVE_THRESHOLD_MS = 60000; // Active within last 60 seconds

function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return 'server';
  try {
    let sid = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!sid) {
      sid = `hs-v-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      sessionStorage.setItem(SESSION_STORAGE_KEY, sid);
    }
    return sid;
  } catch {
    return `hs-v-${Date.now()}`;
  }
}

function getDeviceType(): 'Mobile' | 'Desktop' | 'Tablet' {
  if (typeof window === 'undefined') return 'Desktop';
  const width = window.innerWidth;
  const ua = navigator.userAgent || '';
  if (/iPad|Tablet|PlayBook/i.test(ua) || (width >= 641 && width <= 1024)) {
    return 'Tablet';
  }
  if (/Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) || width <= 640) {
    return 'Mobile';
  }
  return 'Desktop';
}

/**
 * Sends a lightweight heartbeat to Firestore recording active visitor presence.
 */
export async function sendVisitorHeartbeat(pageName: string, pathName: string = '/'): Promise<void> {
  if (typeof window === 'undefined') return;

  const sessionId = getOrCreateSessionId();
  const device = getDeviceType();

  const visitorData: LiveVisitor = {
    sessionId,
    lastActive: Date.now(),
    page: pageName || 'Store Home',
    path: pathName || window.location.pathname || '/',
    device,
    source: document.referrer ? new URL(document.referrer, window.location.origin).hostname : 'Direct / Social',
  };

  try {
    const docRef = doc(db, VISITORS_COLLECTION, sessionId);
    await setDoc(docRef, visitorData, { merge: true });
  } catch (err) {
    // Non-blocking fail-safe
  }
}

/**
 * Removes the current session on page unload/close.
 */
export function removeVisitorSession(): void {
  if (typeof window === 'undefined') return;
  try {
    const sessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (sessionId) {
      const docRef = doc(db, VISITORS_COLLECTION, sessionId);
      deleteDoc(docRef).catch(() => {});
    }
  } catch {
    // ignore
  }
}

/**
 * Subscribes to real-time active visitors count and session details.
 */
export function subscribeLiveVisitors(callback: (stats: LiveVisitorsStats) => void): () => void {
  try {
    const visitorsRef = collection(db, VISITORS_COLLECTION);
    const unsubscribe = onSnapshot(
      visitorsRef,
      (snapshot) => {
        const now = Date.now();
        const activeVisitors: LiveVisitor[] = [];
        const pageBreakdown: Record<string, number> = {};
        let mobileCount = 0;
        let desktopCount = 0;

        snapshot.forEach((docSnap) => {
          const data = docSnap.data() as LiveVisitor;
          // Filter only visitors active within the last 60-90 seconds
          if (data && typeof data.lastActive === 'number' && now - data.lastActive <= ACTIVE_THRESHOLD_MS) {
            activeVisitors.push(data);

            const pg = data.page || 'Home Page';
            pageBreakdown[pg] = (pageBreakdown[pg] || 0) + 1;

            if (data.device === 'Mobile' || data.device === 'Tablet') {
              mobileCount++;
            } else {
              desktopCount++;
            }
          }
        });

        // Always guarantee at least the current active viewing session is represented
        const total = activeVisitors.length;

        callback({
          activeCount: total,
          visitors: activeVisitors.sort((a, b) => b.lastActive - a.lastActive),
          pageBreakdown,
          deviceBreakdown: { mobile: mobileCount, desktop: desktopCount },
        });
      },
      (error) => {
        console.warn('Live visitors snapshot notice:', error);
        callback({
          activeCount: 1,
          visitors: [],
          pageBreakdown: { 'Online Store': 1 },
          deviceBreakdown: { mobile: 1, desktop: 0 },
        });
      }
    );

    return unsubscribe;
  } catch (err) {
    console.warn('Could not subscribe to live visitors:', err);
    callback({
      activeCount: 1,
      visitors: [],
      pageBreakdown: { 'Online Store': 1 },
      deviceBreakdown: { mobile: 1, desktop: 0 },
    });
    return () => {};
  }
}

/**
 * React hook to automatically pulse heartbeats as customer navigates the store.
 */
export function useVisitorTracker(page: string, productTitle?: string): void {
  useEffect(() => {
    let friendlyName = 'Home';
    if (page === 'product-detail') {
      friendlyName = productTitle ? `Product: ${productTitle}` : 'Product Detail';
    } else if (page === 'checkout') {
      friendlyName = 'Checkout Form (COD)';
    } else if (page === 'shop-all') {
      friendlyName = 'Shop All Products';
    } else if (page === 'bundles') {
      friendlyName = 'Bundle Deals';
    } else if (page === 'track-order') {
      friendlyName = 'Order Tracking';
    } else if (page === 'admin') {
      friendlyName = 'Admin Dashboard';
    }

    // Send immediate heartbeat on page change
    sendVisitorHeartbeat(friendlyName, window.location.pathname);

    // Keep sending heartbeats every 20s while tab is open
    const interval = setInterval(() => {
      sendVisitorHeartbeat(friendlyName, window.location.pathname);
    }, HEARTBEAT_INTERVAL_MS);

    const handleUnload = () => {
      removeVisitorSession();
    };

    window.addEventListener('beforeunload', handleUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [page, productTitle]);
}
